// client/src/pages/AddProduct/AddProduct.jsx
import React, { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import "./AddProduct.css";
import API_BASE_URL from "../../apiConfig";
import CLIENT_URL from "../../apiConfigClient";

const AddProduct = () => {
  const [formData, setFormData] = useState({
    itemName: "",
    sku: "",
    qrCode: "", // This will mirror the sku value
    quantity: 0,
    location: "",
  });
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [newProduct, setNewProduct] = useState(null);

  // Update handleChange to set both sku and qrCode when the sku field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "sku") {
      setFormData({
        ...formData,
        sku: value,
        qrCode: value, // Keep QR code in sync with SKU
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === "quantity" ? Number(value) : value,
      });
    }
  };

  const handleFileChange = (e) => {
    setImages(e.target.files);
  };

  const handleLocationSelect = (loc) => {
    setFormData({ ...formData, location: loc });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      // Send the formData where qrCode is already set to sku
      const response = await fetch(`${API_BASE_URL}/inventory`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Error adding product");
      }

      const data = await response.json();
      const productSKU = data.sku;

      if (images && images.length > 0) {
        const formDataImages = new FormData();
        for (let i = 0; i < images.length; i++) {
          formDataImages.append("images", images[i]);
        }

        const imageResponse = await fetch(
          `${API_BASE_URL}/inventory/${productSKU}/images`,
          {
            method: "POST",
            credentials: "include",
            body: formDataImages,
          }
        );

        if (!imageResponse.ok) {
          const errData = await imageResponse.json();
          throw new Error(errData.message || "Error uploading images");
        }

        const imageData = await imageResponse.json();
        data.images = imageData.images; // Update the newProduct with uploaded images
      }

      setNewProduct(data);
      setSuccess("Product added successfully!");

      // Reset form fields
      setFormData({
        itemName: "",
        sku: "",
        qrCode: "",
        quantity: 0,
        location: "",
      });
      setImages([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Use CLIENT_URL from your env configuration for the product URL
  const productUrl = newProduct ? `${CLIENT_URL}/inventory/${newProduct.sku}` : "";

  return (
    <div className="add-product-container">
      <h1>Add New Product</h1>
      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">{success}</p>}
      {!newProduct ? (
        <form onSubmit={handleSubmit} className="add-product-form">
          <div className="form-group">
            <label htmlFor="itemName">Item Name:</label>
            <input
              type="text"
              id="itemName"
              name="itemName"
              value={formData.itemName}
              onChange={handleChange}
              placeholder="Enter product name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="sku">SKU:</label>
            <input
              type="text"
              id="sku"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              placeholder="Enter unique SKU"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="quantity">Quantity:</label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Location:</label>
            <div className="location-buttons">
              {["Biology", "Chemistry", "Electrical", "Mechanical"].map((loc) => (
                <button
                  key={loc}
                  type="button"
                  className={`location-button ${
                    formData.location === loc ? "active" : ""
                  }`}
                  onClick={() => handleLocationSelect(loc)}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="images">Product Images:</label>
            <input
              type="file"
              id="images"
              name="images"
              accept="image/*"
              multiple
              onChange={handleFileChange}
            />
          </div>
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? "Adding Product..." : "Add Product"}
          </button>
        </form>
      ) : (
        <div className="qr-code-section">
          <h2>Product QR Code</h2>
          <p>Scan or print this QR code to access the product details:</p>
          <QRCodeCanvas value={productUrl} size={256} />
          <p>{productUrl}</p>
          <button onClick={handlePrint} className="print-button">
            Print QR Code
          </button>
        </div>
      )}
    </div>
  );
};

export default AddProduct;